/**
 * Formatting helpers for UI display values.
 *
 * Use these in stat cards / KPI tiles to keep "no data" rendering
 * consistent across the app (Customer-Portal, Admin-Dashboard,
 * Mobile-Preview etc.).
 */

/**
 * Formats a stat value for display. Returns `fallback` (default "-")
 * when the value is empty, zero, null, undefined, or NaN.
 *
 * Use for empty-state UI consistency (Tage / Trainings / kcal / kg /
 * Punkte etc. — never show a misleading "0" when there's no data).
 *
 * @example
 *   formatStat(coaching.nutrition_days)            // 0   → "-"
 *   formatStat(stats.points_balance, "0 Pkt")      // null → "0 Pkt"
 *   formatStat(weight, "—")                        // 0   → "—"
 *   formatStat(7)                                  // 7   → 7
 *
 * @param {number|string|null|undefined} value
 * @param {string} [fallback="-"]
 * @returns {number|string} value if truthy and finite, else fallback
 */
export const formatStat = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "number" && (!Number.isFinite(value) || value === 0)) {
    return fallback;
  }
  return value;
};

/**
 * Formats a numeric amount in the Swiss style: 1'234.50.
 * - Apostrophe as thousands separator
 * - Point as decimal separator
 * - Always two decimals
 *
 * Use everywhere a CHF amount is displayed (Admin invoice list / detail,
 * customer portal, transaction history, mobile preview, email-rendered
 * values from the backend etc.) so the UI is consistent with the
 * PDF invoice and QR-bill.
 *
 * @example
 *   formatCHF(1234.5)   // "1'234.50"
 *   formatCHF(152.7)    // "152.70"
 *   formatCHF(null)     // "0.00"
 *
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export const formatCHF = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.00";
  const [whole, dec] = Math.abs(num).toFixed(2).split(".");
  // Apostroph alle 3 Stellen von rechts.
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const sign = num < 0 ? "-" : "";
  return `${sign}${grouped}.${dec}`;
};

